import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import useStyles from '../../../styles/alertForm';
import { defaultSensorsLimit, getTooltipSlotProps } from '../helpers';
import { Analysisoptions as LocationAnalysisOptions } from '../../../services/api';
import { AnalysisOptions } from './AnalysisOptionsContainer';
import { useAppSelector } from '../../../state/store';
import { getCurrentLocation, getSensorsByLocId } from '../../../state/selectors';

interface SensorsFilterProps {
  locValue: LocationAnalysisOptions | null;
  onChange: (value: Partial<AnalysisOptions>) => void;
  allowEditing: boolean;
}

type SensorListMode = 'whiteListSensors' | 'blackListSensors';

function SensorsFilterSection({ locValue, onChange, allowEditing }: SensorsFilterProps) {
  const theme = useTheme();
  const { classes } = useStyles();
  const currentLocation = useAppSelector(getCurrentLocation);
  const locSensorsLength = useAppSelector(getSensorsByLocId).get(currentLocation)?.length;

  // Determine which list is currently active based on existing data.
  // Defaults to whitelist if neither is populated yet.
  const initialMode: SensorListMode =
    locValue?.blackListSensors && locValue.blackListSensors.length > 0
      ? 'blackListSensors'
      : 'whiteListSensors';

  const [mode, setMode] = useState<SensorListMode>(initialMode);
  const [inputValue, setInputValue] = useState('');

  // Keep both lists in local state, seeded from the API value.
  // when toggling between whitelist/blacklist (without editing)
  // never loses the values that were present on initial load
  // is only enforced once the user actually makes an edit.
  const [whiteListSensors, setWhiteListSensors] = useState<string[]>(
    locValue?.whiteListSensors ?? []
  );
  const [blackListSensors, setBlackListSensors] = useState<string[]>(
    locValue?.blackListSensors ?? []
  );

  const activeSensors = mode === 'whiteListSensors' ? whiteListSensors : blackListSensors;

  const handleLocationChange = <K extends keyof LocationAnalysisOptions>(
    key: K,
    val: LocationAnalysisOptions[K]
  ) => {
    (onChange as (value: Partial<LocationAnalysisOptions>) => void)({ [key]: val });
  };

  // Persists an edit to the currently active list, and enforces the
  // "only one list at a time" rule by clearing the inactive list -
  // both locally and on the parent - only at the moment of the edit.
  const commitActiveList = (updated: string[]) => {
    const otherKey: SensorListMode =
      mode === 'whiteListSensors' ? 'blackListSensors' : 'whiteListSensors';

    if (mode === 'whiteListSensors') {
      setWhiteListSensors(updated);
      setBlackListSensors([]);
    } else {
      setBlackListSensors(updated);
      setWhiteListSensors([]);
    }

    handleLocationChange(mode, updated as unknown as LocationAnalysisOptions[SensorListMode]);
    handleLocationChange(otherKey, [] as unknown as LocationAnalysisOptions[SensorListMode]);
  };

  const handleModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: SensorListMode | null
  ) => {
    if (!newMode || newMode === mode) return;
    // Just switch which list is displayed/editable - don't touch any data.
    // The originally-loaded values for both lists stay intact until an
    // actual edit is made.
    setMode(newMode);
    setInputValue('');
  };

  const addSensors = (raw: string) => {
    const newIds = raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (newIds.length === 0) return;

    const merged = Array.from(new Set([...activeSensors, ...newIds]));
    commitActiveList(merged);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addSensors(inputValue);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && inputValue === '' && activeSensors.length > 0) {
      // Quick-delete last chip on backspace when input is empty
      commitActiveList(activeSensors.slice(0, -1));
    }
  };

  const handleChipDelete = (sensorId: string) => {
    commitActiveList(activeSensors.filter((id) => id !== sensorId));
  };

  const listLabel = mode === 'whiteListSensors' ? 'Whitelist Sensors' : 'Blacklist Sensors';
  const listTooltip =
    mode === 'whiteListSensors'
      ? 'Only sensor ids in this list will be considered'
      : 'Sensor ids in this list will not be considered';

  return (
    <Box sx={{ mt: 2, ml: 1 }} className={classes.formContainer}>
      <Typography variant="body1" sx={{ width: '100%' }}>
        for number of sensors, this threshold applies to is
      </Typography>
      <Grid container spacing={2} sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
        <Grid size={{ xs: 6, sm: 6 }}>
          <TextField
            fullWidth
            size="small"
            label="Sensors Limit"
            type="number"
            value={locValue?.sensorsLimit ?? defaultSensorsLimit}
            onChange={(e) =>
              handleLocationChange(
                'sensorsLimit',
                e.target.value === '' ? undefined : Number(e.target.value)
              )
            }
            placeholder="e.g. 10"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.contrastText,
              },
            }}
            slotProps={getTooltipSlotProps('Number of sensors that must match before alerting')}
            disabled={!allowEditing}
          />{' '}
        </Grid>
        <span>
          sensors out of <b>{locSensorsLength} sensors</b>
        </span>
        <Grid size={{ xs: 12, sm: 5 }}>
          {allowEditing && (
            <ToggleButtonGroup
              value={mode}
              exclusive
              size="small"
              onChange={handleModeChange}
              sx={{ mb: 1 }}
            >
              <ToggleButton value="whiteListSensors">Whitelist</ToggleButton>
              <ToggleButton value="blackListSensors">Blacklist</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 7 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {listLabel} (optional)
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: allowEditing ? 1 : 0 }}
          >
            {activeSensors.map((sensorId) => (
              <Chip
                key={sensorId}
                label={sensorId}
                size="small"
                onDelete={allowEditing ? () => handleChipDelete(sensorId) : undefined}
              />
            ))}
          </Stack>

          {allowEditing && (
            <TextField
              fullWidth
              size="small"
              label={`Add ${listLabel.toLowerCase()}`}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleInputKeyDown}
              onBlur={() => {
                if (inputValue.trim()) {
                  addSensors(inputValue);
                  setInputValue('');
                }
              }}
              placeholder="Type a sensor id and press Enter or comma"
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.contrastText,
                },
              }}
              slotProps={getTooltipSlotProps(listTooltip)}
              disabled={!allowEditing}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default SensorsFilterSection;
